// ---------------------------------------------------------------------
// Confirm modal — for destructive / high-consequence actions (delete,
// deploy, discard) instead of native confirm() dialogs.
//
// Usage pattern matches the one added for ImageManager (Settings > Marketing
// Images): a single `confirmOpen` state object holding
// { open, title, message, confirmText, onConfirm, isDangerous }, with a
// helper that sets it and a <ConfirmModal> mounted once near the bottom of
// the page reading that state.
//
// This is a SHARED component (it will replace native confirm() app-wide), so
// it carries full modal a11y: role="alertdialog" + aria-modal + aria-labelledby,
// Escape-to-cancel, and a focus trap scoped to the modal's OWN ref (via the
// shared <Modal> wrapper, never a global document query). The trap defaults
// focus to Cancel — NOT Confirm — because this modal guards destructive
// actions and a stray Enter must not fire the consequential one (Cancel is
// the first focusable element in the panel, which is what the trap focuses
// by default). Focus is restored to the triggering element on close.
//
// Renders through the canonical <Modal> (portal to document.body + true
// fixed inset-0 backdrop) instead of hand-rolling its own — see Modal.jsx's
// header comment for why that matters: an inline `fixed inset-0` modal
// nested inside a `space-y-*` page stack picks up that stack's margin-top
// on its own box, shearing the backdrop's top edge short of the viewport.
// No backdrop-click-to-close here (unchanged from before): a destructive
// confirm shouldn't be dismissible by an accidental stray click.
// ---------------------------------------------------------------------

import React from 'react'
import { AlertTriangle } from 'lucide-react'
import Modal from './Modal'

const ConfirmModal = ({ open, title, message, confirmText, onConfirm, onCancel, isDangerous = false }) => {
  const cancelButtonRef = React.useRef(null)

  return (
    <Modal
      open={open}
      onClose={onCancel}
      role="alertdialog"
      ariaLabelledBy="confirm-title"
      maxWidth="max-w-sm"
      scrollable={false}
      closeOnBackdropClick={false}
      initialFocusRef={cancelButtonRef}
    >
      <div className="p-6">
        {isDangerous && (
          <div className="flex items-start gap-4 mb-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-50">
              <AlertTriangle className="h-5 w-5 text-danger-600" strokeWidth={1.75} />
            </div>
          </div>
        )}
        <h3 id="confirm-title" className="text-lg font-semibold text-gray-900 mb-1">
          {title}
        </h3>
        {/* whitespace-pre-line so multi-line messages migrated verbatim from
            the old native confirm() calls keep their line breaks; capped +
            scrollable for the longer explainers (e.g. the recompute flow). */}
        <div className="text-sm text-gray-600 mb-6 whitespace-pre-line max-h-[55vh] overflow-y-auto">
          {message}
        </div>
        <div className="flex justify-end gap-2">
          <button ref={cancelButtonRef} onClick={onCancel} className="btn-secondary btn-sm">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={isDangerous ? 'btn-danger btn-sm' : 'btn-primary btn-sm'}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default ConfirmModal
